import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material/styles";

import { primary } from "app/themes/colors";

import type { CoachSessionStatus } from "../medical-scribe.slice";

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.35; }
  100% { opacity: 1; }
`;

type CoachStatusCardProps = {
  status: CoachSessionStatus;
  onTurnOff: () => void;
};

/**
 * Live coaching status card in the expanded recording widget (ENG-1705).
 * Renders only while coaching is activating or active — never for idle/off,
 * so a failed activation leaves no trace. The undo toast for the off-switch
 * lives in the global action snackbar (dispatched by useLiveCoaching), not here.
 */
export default function CoachStatusCard({
  status,
  onTurnOff
}: CoachStatusCardProps) {
  if (status !== "activating" && status !== "active") {
    return null;
  }

  return (
    <Box
      data-testid="coach-status-card"
      sx={{
        width: "100%",
        backgroundColor: "background.paper",
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "divider",
        px: 1.5,
        py: 1,
        mb: 0.5
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap={0.75}>
          <TipsAndUpdatesOutlinedIcon
            sx={{ fontSize: 16, color: primary[300] }}
          />
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Live Coaching
          </Typography>
        </Stack>
        {status === "active" && (
          <Switch
            size="small"
            checked
            onChange={onTurnOff}
            inputProps={{ "aria-label": "Turn off live coaching" }}
          />
        )}
      </Stack>
      <Stack direction="row" alignItems="center" gap={0.75}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: primary[300],
            animation: `${pulse} 2s ease-in-out infinite`
          }}
        />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {status === "activating"
            ? "Activating coaching…"
            : "Listening for coaching moments…"}
        </Typography>
      </Stack>
    </Box>
  );
}
